import { createSlice, createSelector } from "@reduxjs/toolkit";
import { coreApi } from "api/core";
import { setShowMessage } from "redux/slices/uiSlice";

const baseUrl = "/diagnostics";

const diagnosticsSelector = (state) => state.diagnostics;

const initialState = {
  settings: null,
  feedbacks: [],
  feedbackPagination: null,
  selectedFeedback: null,
  mobileLogs: [],
  mobileLogsPagination: null,
  mobileLogRoutes: [],
  mobileLogRoutesPagination: null,
  selectedMobileLog: null,
  loading: false,
};

const diagnosticsSlice = createSlice({
  name: "diagnostics",
  initialState,
  reducers: {
    setSettings: (state, action) => {
      state.settings = action.payload;
    },
    setFeedbacks: (state, action) => {
      state.feedbacks = action.payload.data || [];
      state.feedbackPagination = action.payload.pagination || null;
    },
    setSelectedFeedback: (state, action) => {
      state.selectedFeedback = action.payload;
    },
    setMobileLogs: (state, action) => {
      state.mobileLogs = action.payload.data || [];
      state.mobileLogsPagination = action.payload.pagination || null;
    },
    setMobileLogRoutes: (state, action) => {
      state.mobileLogRoutes = action.payload.data || [];
      state.mobileLogRoutesPagination = action.payload.pagination || null;
    },
    setSelectedMobileLog: (state, action) => {
      state.selectedMobileLog = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
});

export const {
  setSettings,
  setFeedbacks,
  setSelectedFeedback,
  setMobileLogs,
  setMobileLogRoutes,
  setSelectedMobileLog,
  setLoading,
} = diagnosticsSlice.actions;

export const getDiagnosticsSettings = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const res = await coreApi.fetch(`${baseUrl}/settings`);
    dispatch(setSettings(res.data));
  } catch (error) {
    dispatch(
      setShowMessage({
        description: "Failed to load diagnostics settings",
        type: "error",
      }),
    );
  } finally {
    dispatch(setLoading(false));
  }
};

export const updateWebDiagnosticsSettings = (payload) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const res = await coreApi.put(`${baseUrl}/settings/web`, payload);
    dispatch(setSettings(res.data));
    dispatch(
      setShowMessage({
        description: "Feedback email settings saved",
        type: "success",
      }),
    );
    return res.data;
  } catch (error) {
    dispatch(
      setShowMessage({
        description: "Failed to save feedback email settings",
        type: "error",
      }),
    );
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

export const updateMobileDiagnosticsSettings = (payload) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const res = await coreApi.put(`${baseUrl}/settings/mobile`, payload);
    dispatch(setSettings(res.data));
    dispatch(
      setShowMessage({
        description: "Mobile settings saved",
        type: "success",
      }),
    );
    return res.data;
  } catch (error) {
    dispatch(
      setShowMessage({
        description: "Failed to save mobile settings",
        type: "error",
      }),
    );
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

export const getFeedbacks = (params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const query = new URLSearchParams(params).toString();
    const res = await coreApi.fetch(
      `${baseUrl}/feedbacks${query ? `?${query}` : ""}`,
    );
    dispatch(setFeedbacks(res));
  } catch (error) {
    dispatch(
      setShowMessage({
        description: "Failed to load feedbacks",
        type: "error",
      }),
    );
  } finally {
    dispatch(setLoading(false));
  }
};

export const getFeedback = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const res = await coreApi.fetch(`${baseUrl}/feedbacks/${id}`);
    dispatch(setSelectedFeedback(res.data));
    return res.data;
  } catch (error) {
    dispatch(
      setShowMessage({
        description: "Failed to load feedback",
        type: "error",
      }),
    );
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

export const getMobileLogs = (params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== "" && v != null),
    ).toString();
    const res = await coreApi.fetch(
      `${baseUrl}/mobile-logs${query ? `?${query}` : ""}`,
    );
    dispatch(setMobileLogs(res));
  } catch (error) {
    dispatch(
      setShowMessage({
        description: "Failed to load mobile logs",
        type: "error",
      }),
    );
  } finally {
    dispatch(setLoading(false));
  }
};

export const getMobileLogRoutes = (params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== "" && v != null),
    ).toString();
    const res = await coreApi.fetch(
      `${baseUrl}/mobile-logs/routes${query ? `?${query}` : ""}`,
    );
    dispatch(setMobileLogRoutes(res));
  } catch (error) {
    dispatch(
      setShowMessage({
        description: "Failed to load mobile log routes",
        type: "error",
      }),
    );
  } finally {
    dispatch(setLoading(false));
  }
};

export const getMobileLog = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const res = await coreApi.fetch(`${baseUrl}/mobile-logs/${id}`);
    dispatch(setSelectedMobileLog(res.data));
    return res.data;
  } catch (error) {
    dispatch(
      setShowMessage({
        description: "Failed to load mobile log",
        type: "error",
      }),
    );
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

export const selectDiagnosticsSettings = createSelector(
  diagnosticsSelector,
  (state) => state.settings,
);
export const selectDiagnosticsFeedbacks = createSelector(
  diagnosticsSelector,
  (state) => state.feedbacks,
);
export const selectDiagnosticsFeedbackPagination = createSelector(
  diagnosticsSelector,
  (state) => state.feedbackPagination,
);
export const selectSelectedFeedback = createSelector(
  diagnosticsSelector,
  (state) => state.selectedFeedback,
);
export const selectMobileLogs = createSelector(
  diagnosticsSelector,
  (state) => state.mobileLogs,
);
export const selectMobileLogsPagination = createSelector(
  diagnosticsSelector,
  (state) => state.mobileLogsPagination,
);
export const selectMobileLogRoutes = createSelector(
  diagnosticsSelector,
  (state) => state.mobileLogRoutes,
);
export const selectMobileLogRoutesPagination = createSelector(
  diagnosticsSelector,
  (state) => state.mobileLogRoutesPagination,
);
export const selectSelectedMobileLog = createSelector(
  diagnosticsSelector,
  (state) => state.selectedMobileLog,
);
export const selectDiagnosticsLoading = createSelector(
  diagnosticsSelector,
  (state) => state.loading,
);

export default diagnosticsSlice.reducer;
